import { useEffect, useMemo, useState } from 'react';

import { getTabValue, getTabs } from './StaffingUtils';

import Tabs from '@/components/Tabs';
import { ApiEndpoints, V3Endpoints } from '@/constants/urls';
import { Skill } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import axiosClient, { axiosRubyClient } from '@/util/axios/axiosClient';
import { WorkersCount } from '@/util/axios/types';
import useAuth from '@/hooks/useAuth';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useFeatureValue } from '@growthbook/growthbook-react';

type StaffingTabsProps = {
  tab: number;
  setTab: (tab: number) => void;
  skill: Partial<Skill>;
  isLTA: boolean;
  custId: number | string;
  addressId: number | string;
  jobTaxType: string;
  badgeIds: string | null;
  backgroundCheckRequired?: boolean;
  jobId?: number | string | null;
};

const StaffingTabs = ({
  tab,
  setTab,
  skill,
  isLTA,
  custId,
  addressId,
  jobTaxType,
  badgeIds = null,
  backgroundCheckRequired,
  jobId = null,
}: StaffingTabsProps) => {
  const [counts, setCounts] = useState<WorkersCount>();
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();

  const TABS = useMemo(
    () => getTabs({ counts, isTrained: !!skill?.gravyTrained, isClientAdmin }),
    [counts, skill],
  );

  const jobFulfillment = useFeatureValue(FEATURE_TOGGLE.JobFulfillment, false);

  const { logEvent } = useAnalytics();

  useEffect(() => {
    let isCancelled = false; // Prevent setting state if component unmounts or effect re-runs

    const fetchWorkersCounts = async () => {
      try {
        const res = jobFulfillment
          ? await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_COUNT, {
              params: {
                skillId: Number(skill?.id),
                customerId: Number(custId),
                addressId: Number(addressId),
                jobId: jobId,
                workerType: isLTA ? 'lta' : 'gig',
                taxType: jobTaxType || 'all',
                badgeIds: badgeIds,
                backgroundCheckRequired: backgroundCheckRequired,
              },
            })
          : await axiosClient.post(V3Endpoints.GET_WORKERS_COUNT, {
              skillId: Number(skill?.id),
              customerId: Number(custId),
              addressId: Number(addressId),
              workerType: isLTA ? 'lta' : 'gig',
              taxType: jobTaxType || 'all',
              badgeIds: badgeIds,
              backgroundCheckRequired: backgroundCheckRequired,
            });

        // Only update state if the effect hasn't been cancelled and we have valid data
        if (!isCancelled && res.data?.data) {
          setCounts((prevCounts) => {
            // Check if the new data is different from previous to avoid unnecessary re-renders
            const newData = res.data.data;
            if (JSON.stringify(prevCounts) === JSON.stringify(newData)) {
              return prevCounts; // Return previous state if data is identical
            }
            return newData;
          });
        }
      } catch (e) {
        if (!isCancelled) {
          console.error(e);
        }
      }
    };

    fetchWorkersCounts();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [
    skill?.id,
    custId,
    addressId,
    jobId,
    isLTA,
    jobTaxType,
    badgeIds,
    backgroundCheckRequired,
    jobFulfillment,
  ]);

  return (
    <div
      className="hide-scroll"
      style={{ width: '100%', overflow: 'auto hidden' }}
    >
      <Tabs
        fit
        minWidth="216px"
        selected={tab}
        showTabOnMobile={true}
        tabs={TABS}
        onSelect={(tab: number) => {
          logEvent(getTabValue(tab, !!skill.gravyTrained));
          setTab(tab);
        }}
      />
    </div>
  );
};

export default StaffingTabs;
